import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  LayoutDashboard, Package, FileText, Calendar,
  LogOut, Building2, MapPin, Download, ChevronRight,
  CheckCircle2, Clock, TrendingUp, User, Mail, Phone,
  AlertCircle, ExternalLink
} from 'lucide-react';

const STATUS_CONFIG = {
  müsait: { label: 'Müsait', cls: 'bg-green-100 text-green-800 border-green-200' },
  rezerve: { label: 'Rezerve', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  satıldı: { label: 'Satıldı', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const CONTRACT_STATUS = {
  taslak: { label: 'Taslak', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  imzalandı: { label: 'İmzalandı', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  iptal: { label: 'İptal', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Genel Bakış', icon: LayoutDashboard },
  { key: 'stands', label: 'Standlarım', icon: Package },
  { key: 'contracts', label: 'Sözleşmelerim', icon: FileText },
  { key: 'exhibitions', label: 'Fuar Bilgileri', icon: Calendar },
];

function formatDate(str) {
  if (!str) return '-';
  try {
    const d = new Date(str);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return str; }
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CompanyDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [section, setSection] = useState('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => { fetchPortal(); }, []); // eslint-disable-line

  const fetchPortal = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/company/portal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error('Portal verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const downloadContract = async (contractId, contractName) => {
    setDownloadingId(contractId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/company/my-contracts/${contractId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contractName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF indirilemedi');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const stands = data?.stands || [];
  const contracts = data?.contracts || [];
  const exhibitions = data?.exhibitions || [];
  const companies = data?.companies || [];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="company-portal">
      {/* Sidebar */}
      <aside
        data-testid="company-sidebar"
        className={`${sidebarOpen ? 'w-60' : 'w-16'} bg-white border-r border-slate-200 flex flex-col transition-all duration-200 shrink-0`}
      >
        {/* Logo area */}
        <div className="h-16 flex items-center px-4 border-b border-slate-100 gap-3">
          <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          {sidebarOpen && (
            <div className="overflow-hidden">
              <p className="text-sm font-bold text-gray-900 truncate">Firma Portalı</p>
              <p className="text-xs text-gray-400 truncate">{user?.name}</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`nav-${key}`}
              onClick={() => setSection(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${section === key
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-gray-600 hover:bg-slate-50 hover:text-gray-900'}`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {sidebarOpen && <span>{label}</span>}
              {sidebarOpen && section === key && <ChevronRight className="w-3 h-3 ml-auto" />}
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-2 border-t border-slate-100">
          <button
            data-testid="company-logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {sidebarOpen && <span>Çıkış Yap</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-4 sticky top-0 z-10">
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-slate-100 transition-colors"
          >
            <LayoutDashboard className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-semibold text-gray-900">
              {NAV_ITEMS.find(n => n.key === section)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <User className="w-4 h-4" />
            <span>{user?.email}</span>
          </div>
        </header>

        <div className="p-6">
          {/* DASHBOARD */}
          {section === 'dashboard' && (
            <div className="space-y-6" data-testid="section-dashboard">
              {/* Welcome */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Hoşgeldiniz, {user?.name}</h2>
                <p className="text-gray-500 mt-1">Fuar katılım bilgilerinize buradan ulaşabilirsiniz.</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Package} label="Toplam Stand" value={stats.total_stands || 0} accent="bg-teal-500" />
                <StatCard icon={CheckCircle2} label="Satın Alınan" value={stats.sold || 0} accent="bg-blue-500" />
                <StatCard icon={Clock} label="Rezerve" value={stats.reserved || 0} accent="bg-amber-500" />
                <StatCard icon={FileText} label="Sözleşme" value={stats.total_contracts || 0} accent="bg-purple-500" />
              </div>

              {/* Quick Stand List */}
              {stands.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-teal-600" /> Son Standlarım
                      </CardTitle>
                      <button
                        onClick={() => setSection('stands')}
                        className="text-xs text-teal-600 hover:underline flex items-center gap-1"
                      >
                        Tümü <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {stands.slice(0, 4).map(s => {
                        const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.müsait;
                        return (
                          <div key={s.id} className="flex items-center justify-between px-6 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{s.stand_number || '-'}</p>
                              <p className="text-xs text-gray-500">{s.exhibition_name}</p>
                            </div>
                            <Badge className={`text-xs border ${sc.cls}`}>{sc.label}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Contracts */}
              {contracts.filter(c => c.status === 'imzalandı').length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-emerald-600" /> İmzalı Sözleşmelerim
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {contracts.filter(c => c.status === 'imzalandı').slice(0, 3).map(c => (
                        <div key={c.id} className="flex items-center justify-between px-6 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{c.template_name}</p>
                            <p className="text-xs text-gray-500">{c.exhibition_name} · Stand {c.stand_number}</p>
                          </div>
                          <button
                            onClick={() => downloadContract(c.id, `${c.template_name} - ${c.stand_number}`)}
                            disabled={downloadingId === c.id}
                            className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors"
                            title="PDF İndir"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {stands.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Henüz atanmış bir standınız yok.</p>
                    <p className="text-sm text-gray-400 mt-1">Organizatörünüzle iletişime geçin.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* STANDS */}
          {section === 'stands' && (
            <div className="space-y-4" data-testid="section-stands">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">Standlarım ({stands.length})</h2>
              </div>

              {stands.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Henüz atanmış bir standınız yok.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {stands.map(s => {
                    const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.müsait;
                    const area = (s.width || 0) * (s.height || 0);
                    const relatedContracts = contracts.filter(c => c.stand_id === s.id);
                    return (
                      <Card key={s.id} data-testid={`stand-card-${s.id}`} className="overflow-hidden hover:shadow-md transition-shadow">
                        <div className="h-1.5 w-full" style={{
                          backgroundColor: s.status === 'müsait' ? '#10b981' : s.status === 'rezerve' ? '#f59e0b' : '#3b82f6'
                        }} />
                        <CardHeader className="pb-2 pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{s.stand_number || '—'}</CardTitle>
                              {s.stand_name && (
                                <p className="text-sm text-gray-600 mt-0.5">{s.stand_name}</p>
                              )}
                            </div>
                            <Badge className={`text-xs border ${sc.cls}`}>{sc.label}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 pb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate">{s.exhibition_name}</span>
                          </div>
                          {s.hall_name && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Building2 className="w-3.5 h-3.5 text-gray-400" />
                              <span>{s.hall_name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span>{s.width}m × {s.height}m = <strong>{area}m²</strong></span>
                          </div>
                          {s.exhibition_start && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              <span>{formatDate(s.exhibition_start)} – {formatDate(s.exhibition_end)}</span>
                            </div>
                          )}
                          {relatedContracts.length > 0 && (
                            <div className="pt-1 border-t border-slate-100">
                              {relatedContracts.slice(0, 1).map(c => {
                                const cs = CONTRACT_STATUS[c.status] || CONTRACT_STATUS.taslak;
                                return (
                                  <div key={c.id} className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">{c.template_name}</span>
                                    <Badge className={`text-xs border ${cs.cls}`}>{cs.label}</Badge>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CONTRACTS */}
          {section === 'contracts' && (
            <div className="space-y-4" data-testid="section-contracts">
              <h2 className="text-lg font-bold text-gray-900">Sözleşmelerim ({contracts.length})</h2>

              {contracts.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Henüz oluşturulmuş bir sözleşmeniz yok.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <div className="divide-y divide-slate-100">
                    {contracts.map(c => {
                      const cs = CONTRACT_STATUS[c.status] || CONTRACT_STATUS.taslak;
                      return (
                        <div key={c.id} data-testid={`contract-row-${c.id}`}
                          className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
                          <div className={`w-2 h-10 rounded-full ${c.status === 'imzalandı' ? 'bg-emerald-400' : c.status === 'iptal' ? 'bg-red-400' : 'bg-gray-300'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{c.template_name}</p>
                            <p className="text-sm text-gray-500 truncate">
                              {c.exhibition_name} · Stand {c.stand_number}
                            </p>
                            <p className="text-xs text-gray-400">{formatDate(c.generated_at)}</p>
                          </div>
                          <Badge className={`text-xs border ${cs.cls} shrink-0`}>{cs.label}</Badge>
                          <button
                            data-testid={`download-contract-${c.id}`}
                            onClick={() => downloadContract(c.id, `${c.template_name} - ${c.stand_number}`)}
                            disabled={downloadingId === c.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors disabled:opacity-50 shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                            {downloadingId === c.id ? 'İndiriliyor...' : 'PDF'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* EXHIBITIONS */}
          {section === 'exhibitions' && (
            <div className="space-y-4" data-testid="section-exhibitions">
              <h2 className="text-lg font-bold text-gray-900">Fuar Bilgileri ({exhibitions.length})</h2>

              {exhibitions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Fuar bilgisi bulunamadı.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {exhibitions.map(ex => {
                    const exStands = stands.filter(s => s.exhibition_id === ex.id);
                    return (
                      <Card key={ex.id} data-testid={`exhibition-card-${ex.id}`} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">{ex.name}</CardTitle>
                          {ex.description && (
                            <p className="text-sm text-gray-500 mt-1">{ex.description}</p>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {(ex.start_date || ex.end_date) && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Calendar className="w-4 h-4 text-teal-500" />
                              <span>{formatDate(ex.start_date)} – {formatDate(ex.end_date)}</span>
                            </div>
                          )}
                          {ex.venue && (
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <MapPin className="w-4 h-4 text-teal-500" />
                              <span>{ex.venue}</span>
                            </div>
                          )}
                          {exStands.length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <p className="text-xs text-gray-500 mb-2">Bu fuardaki standlarım:</p>
                              <div className="flex flex-wrap gap-2">
                                {exStands.map(s => {
                                  const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.müsait;
                                  return (
                                    <span key={s.id} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${sc.cls}`}>
                                      <Package className="w-2.5 h-2.5" />
                                      {s.stand_number || '-'}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <button
                            onClick={() => window.open(`/expo/${ex.id}/map`, '_blank')}
                            className="flex items-center gap-1.5 text-xs text-teal-600 hover:underline mt-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Fuar haritasını görüntüle
                          </button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Company Info */}
              {companies.length > 0 && (
                <Card className="mt-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-teal-600" />
                      Firma Bilgilerim
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {companies.map(c => (
                        <div key={c.id} className="p-4 bg-slate-50 rounded-lg space-y-2">
                          <p className="font-semibold text-gray-900">{c.name}</p>
                          {c.email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <span>{c.email}</span>
                            </div>
                          )}
                          {c.phone && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              <span>{c.phone}</span>
                            </div>
                          )}
                          {c.address && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MapPin className="w-3.5 h-3.5 text-gray-400" />
                              <span>{c.address}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
